import { Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, delay, map, scan, shareReplay, tap } from 'rxjs/operators';
import { NotificationService } from 'src/app/services/notification/notification.service';

import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AlertController, ModalController, RefresherCustomEvent } from '@ionic/angular';
import { ApiResponse, InfiniteScrollCustomEvent } from '@shared/models';
import { Billing, EditModel, Invoice } from '@shared/models/billing.model';
import { convertBlobToBase64, getMappedPaymentMethod } from '@shared/utils';

import { BillingSelectionComponent } from '../billing-selection/billing-selection.component';
import { ProfileBillsAddPage } from '../profile-bills-add/profile-bills-add.page';
import { BillingService } from './services/billing.service';

export type Transaction = Invoice & { title: string };

@Component({
  selector: 'app-billing-info',
  templateUrl: './billing-info.component.html',
  styleUrls: ['./billing-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingInfoComponent {
  show = 'invoices';
  loadingInvoices = true;
  loadingBillings = true;
  private loadInvoicesSubject = new Subject<number>();
  private loadBillingssSubject = new Subject<number>();
  private infiniteScroll: InfiniteScrollCustomEvent | null = null;
  invoices$ = this.loadInvoicesSubject.asObservable().pipe(
    delay(0),
    tap(() => (this.loadingInvoices = true)),
    concatMap((page) => this.getTransactions(page)),
    scan((acc, response) => this.accumulateResults(acc, response)),
    tap(() => {
      this.loadingInvoices = false;
      this.infiniteScroll?.target.complete();
    }),
    shareReplay({ bufferSize: 1, refCount: false })
  );
  billings$ = this.loadBillingssSubject.asObservable().pipe(
    delay(0),
    tap(() => (this.loadingBillings = true)),
    concatMap(() => this.getBillings(1)),
    scan((acc, response) => this.accumulateResults(acc, response)),
    tap(() => {
      this.loadingBillings = false;
      this.infiniteScroll?.target.complete();
    }),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  constructor(
    private billingService: BillingService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private notificationService: NotificationService
  ) {}

  ionViewDidEnter() {
    this.refresh();
  }

  doRefresh(refresher: RefresherCustomEvent) {
    if (this.show === 'invoices') {
      this.loadInvoicesSubject.next(1);
    } else {
      this.loadBillingssSubject.next(1);
    }
    setTimeout(() => {
      refresher.target.complete();
    }, 1000);
  }

  doInfinite<T>(infiniteScroll: InfiniteScrollCustomEvent, list: ApiResponse<T[]>) {
    if (list.current_page === list.last_page) {
      infiniteScroll.target.complete();
      return;
    }
    this.infiniteScroll = infiniteScroll;
    if (this.show === 'invoices') {
      this.loadInvoicesSubject.next(list.current_page ? list.current_page + 1 : 1);
    } else {
      this.loadBillingssSubject.next(list.current_page ? list.current_page + 1 : 1);
    }
  }

  addBillingInfo(billing: Billing | null = null) {
    this.modalCtrl
      .create({
        component: ProfileBillsAddPage,
        componentProps: {
          billing,
        },
      })
      .then((modal) => {
        modal.present();
        modal.onDidDismiss().then((data) => {
          this.refresh();
        });
      });
  }

  deleteBillingInfo(billing: EditModel<Billing>) {
    this.alertCtrl
      .create({
        header: '¿Estás seguro?',
        message: '¿Quieres eliminar este registro?',
        buttons: [
          {
            text: 'Cancelar',
            role: 'cancel',
          },
          {
            text: 'Eliminar',
            handler: () => {
              this.billingService.deleteBilling(billing.id).subscribe(() => {
                this.notificationService.presentToast('Se ha eliminado el registro correctamente', 3000, 'top');
                this.refresh();
              });
            },
          },
        ],
      })
      .then((alert) => {
        alert.present();
      });
  }

  requestInvoice(invoiceId: number, billingId: number, type: 'saldo' | 'suscription') {
    this.billingService
      .createInvoice({
        billing_id: billingId,
        saldo_payment_id: type === 'saldo' ? invoiceId : null,
        suscription_payment_id: type === 'suscription' ? invoiceId : null,
      })
      .pipe(
        tap(() => {
          this.notificationService.presentToast('Se ha solicitado la factura correctamente', 3000, 'top');
          this.refresh();
        }),
        catchError((error) => {
          const message =
            error.error.message || 'Ha ocurrido un error al solicitar factura. Favor de intentar nuevamente.';
          this.notificationService.presentToast(message, 3000, 'top');
          return of(null);
        })
      )
      .subscribe();
  }

  selectBilling(invoice: Invoice) {
    this.modalCtrl
      .create({
        component: BillingSelectionComponent,
      })
      .then((modal) => {
        modal.present();
        modal.onDidDismiss().then((event) => {
          if (event.data?.billing?.id && invoice.id) {
            this.requestInvoice(invoice.id, event.data.billing.id, invoice.type);
          }
        });
      });
  }

  async download(url: string) {
    const fileName = url.split('/').pop();

    const bob = await fetch(url).then((x) => x.blob());
    const base64 = await convertBlobToBase64(bob);

    console.log(fileName, base64);

    // Save file to filesystem
    // const resultSaveFile = await Filesystem.writeFile({
    //   data: base64,
    //   path: fileName,
    //   directory: Directory.Data,
    //   recursive: true,
    // });

    // if (resultSaveFile) {
    //   this.helper.toast('Se ha descargado el archivo correctamente', 'Bien hecho');
    // } else {
    //   this.helper.toast('No se ha podido descargar el archivo correctamente', 'Lo siento');
    // }
  }

  private getTransactions(page: number): Observable<ApiResponse<Transaction[]>> {
    return this.billingService.getInvoices({ page }).pipe(
      map((response) => ({
        ...response,
        data: response.data.map((invoice) => ({
          ...invoice,
          title:
            (invoice.annotations ? invoice.annotations : `Abono a Wallet`) +
            ` por ${getMappedPaymentMethod(invoice.method)}`,
        })),
      })),
      catchError(() => of({ data: [] }))
    );
  }

  private getBillings(page: number): Observable<ApiResponse<Billing[]>> {
    return this.billingService.getBillings({ page }).pipe(catchError(() => of({ data: [] })));
  }

  private refresh() {
    this.loadInvoicesSubject.next(1);
    this.loadBillingssSubject.next(1);
  }

  private accumulateResults<T>(acc: ApiResponse<T[]>, response: ApiResponse<T[]>): ApiResponse<T[]> {
    if (!response) {
      return acc;
    }
    if (response.current_page === 1) {
      return response;
    }
    return {
      ...response,
      data: [...acc.data, ...response.data],
    };
  }
}
